import { useCallback, useMemo, useState } from "react";
import { Alert, Box, Link, Typography } from "@mui/material";
import { Redirect, useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ContainerWrapper from "../../../components/ContainerWrapper/ContainerWrapper";
import ActionButton from "../../../components/Button/ActionButton";
import Loading from "../../../components/Loading/Loading";
import HelpButton from "../../../components/payments/HelpContent/HelpButton";
import ConfigurationSection from "../../../components/payments/ConfigurationSection/ConfigurationSection";
import DocumentUploadField from "../../../components/payments/DocumentUploadField/DocumentUploadField";
import DocumentGroupSection from "../../../components/payments/DocumentGroupSection/DocumentGroupSection";
import usePaymentConfigurations from "../../../hooks/usePaymentConfigurations";
import useUpdatePaymentConfiguration from "../../../hooks/useUpdatePaymentConfiguration";
import useDocumentUpload from "../../../hooks/useDocumentUpload";
import {
  DocumentGroupEntry,
  findSingleDocument,
  isGroupComplete,
  rehydrateGroup,
  requiredDocumentsFor,
  requiredGroupsFor
} from "./documentMatrix";
import {
  DOCUMENT_GROUP_KEYS,
  DocumentGroupKey,
  SINGLE_DOCUMENT_GROUP_ID
} from "../../../types/payments";
import { PAYMENTS_HELP_CONTENT, PAYMENTS_MANUAL_URL } from "../../../constants";
import { semanticColors } from "../../../utils/colors";
import {
  CONFIGURATIONS_PATH,
  configurationInReviewPath,
  configurationPath
} from "../../../utils/paymentsRoutes";
import { typography } from "../../../utils/typography";

type LocalGroupIndices = Record<DocumentGroupKey, number[]>;

const noLocalEntries: LocalGroupIndices = {
  [DOCUMENT_GROUP_KEYS.BENEFICIARIES]: [],
  [DOCUMENT_GROUP_KEYS.LEGAL_REPRESENTATION]: []
};

/**
 * KYC document submission.
 *
 * Which documents are required depends on the legal form of the billing entity,
 * which is why the company type cannot be changed once the configuration
 * exists. Everything already uploaded is rehydrated from the configuration, so
 * the screen can be left and reopened without losing progress.
 */
export default function PaymentConfigurationDocuments() {
  const { t } = useTranslation();
  const history = useHistory();
  const { configId } = useParams<{ configId: string }>();
  const { configurations, loading, refetch } = usePaymentConfigurations();
  const {
    markDocumentsReady,
    loading: confirming
  } = useUpdatePaymentConfiguration();
  const { remove } = useDocumentUpload();

  // Entries the professional has added but not documented yet: they exist only
  // here until at least one of their documents is uploaded.
  const [localIndices, setLocalIndices] = useState<LocalGroupIndices>(
    noLocalEntries
  );
  const [error, setError] = useState<string | null>(null);

  const configuration = useMemo(
    () => configurations.find(item => item.id === configId),
    [configurations, configId]
  );

  /**
   * Documents are only collected while the KYC stage is open. Once the
   * submission is with Easypay (REVIEWING) or approved (DONE), the screen shows
   * what is on file and offers nothing.
   *
   * This is not cosmetic. The API answers an upload against a closed
   * configuration with 200 and a usable upload policy, but registers no
   * document: the file reaches storage and then every call that addresses it
   * answers 404, which reads to the professional as "the upload failed" on a
   * document that is, in fact, already approved.
   */
  const readOnly =
    configuration?.easypayState === "REVIEWING" ||
    configuration?.easypayState === "DONE";

  const entriesFor = useCallback(
    (groupKey: DocumentGroupKey): DocumentGroupEntry[] => {
      const stored = rehydrateGroup(configuration?.easypayDocuments, groupKey);
      const storedIndices = new Set(stored.map(entry => entry.index));
      const extras = localIndices[groupKey]
        .filter(index => !storedIndices.has(index))
        .map(index => ({ index, documents: {} }));
      return [...stored, ...extras].sort((a, b) => a.index - b.index);
    },
    [configuration?.easypayDocuments, localIndices]
  );

  const addEntry = useCallback(
    (groupKey: DocumentGroupKey) => {
      const current = entriesFor(groupKey);
      // Indices are never reused, so removing an entry cannot silently
      // re-address the documents already uploaded by the remaining ones.
      const nextIndex = current.length
        ? Math.max(...current.map(entry => entry.index)) + 1
        : 0;
      setLocalIndices(indices => ({
        ...indices,
        [groupKey]: [...indices[groupKey], nextIndex]
      }));
    },
    [entriesFor]
  );

  const removeEntry = useCallback(
    async (groupKey: DocumentGroupKey, entry: DocumentGroupEntry) => {
      setError(null);
      try {
        // Delete the entry's documents too, otherwise they would stay attached
        // to the configuration and be submitted to Easypay as orphans.
        const documents = Object.values(entry.documents);
        for (const document of documents) {
          if (document) await remove(configId, document.id);
        }
        setLocalIndices(indices => ({
          ...indices,
          [groupKey]: indices[groupKey].filter(index => index !== entry.index)
        }));
        await refetch();
      } catch {
        setError(t("paymentDocuments.removeError"));
      }
    },
    [configId, remove, refetch, t]
  );

  const requiredDocuments = useMemo(
    () =>
      configuration
        ? requiredDocumentsFor(configuration.easypayCompanyType)
        : [],
    [configuration]
  );
  const requiredGroups = useMemo(
    () =>
      configuration ? requiredGroupsFor(configuration.easypayCompanyType) : [],
    [configuration]
  );

  const allDocumentsUploaded = useMemo(() => {
    if (!configuration) return false;
    const singlesDone = requiredDocuments.every(documentType =>
      findSingleDocument(configuration.easypayDocuments, documentType)
    );
    const groupsDone = requiredGroups.every(groupKey =>
      isGroupComplete(entriesFor(groupKey))
    );
    return singlesDone && groupsDone;
  }, [configuration, requiredDocuments, requiredGroups, entriesFor]);

  const onConfirm = useCallback(async () => {
    setError(null);
    try {
      await markDocumentsReady(configId);
      await refetch();
      // Not the configuration hub: it lists the requirements as outstanding,
      // which reads as though the submission had not happened.
      history.push(configurationInReviewPath(configId));
    } catch {
      setError(t("paymentDocuments.confirmError"));
    }
  }, [markDocumentsReady, configId, refetch, history, t]);

  // Only while there is nothing to show. The configurations are refetched
  // whenever the window regains focus, and swapping the form for a spinner on
  // every one of those would unmount the slots — throwing away any file the
  // professional had picked but not sent yet.
  if (loading && !configuration) {
    return (
      <ContainerWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" mt={10}>
          <Loading />
        </Box>
      </ContainerWrapper>
    );
  }

  if (!configuration) {
    return (
      <ContainerWrapper>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2
          }}
        >
          <Typography
            sx={{ ...typography.body.l, color: semanticColors.text.primary }}
          >
            {t("paymentConfigurationDetail.notFound")}
          </Typography>
          <ActionButton
            label={t("paymentConfigurationDetail.backToList")}
            onClick={() => history.push(CONFIGURATIONS_PATH)}
            variant="secondary"
          />
        </Box>
      </ContainerWrapper>
    );
  }

  /**
   * Already submitted and not yet approved: the professional has nothing to do
   * here, so the screen they get is the one that says so.
   *
   * Keyed on `documentsReady` rather than on the review state, because the two
   * move separately — the flag is set when the professional confirms, while
   * `easypayState` is moved by the backend and by Easypay afterwards. A
   * configuration can sit at `documentsReady: true` with the state still
   * untouched, and that is exactly the case this covers.
   *
   * An approved configuration is excluded: there the documents are worth
   * looking at, and the screen shows them read-only.
   */
  if (configuration.documentsReady && configuration.easypayState !== "DONE") {
    return <Redirect to={configurationInReviewPath(configId)} />;
  }

  const groupLabels = {
    [DOCUMENT_GROUP_KEYS.BENEFICIARIES]: {
      title: t("paymentDocuments.beneficiaries"),
      add: t("paymentDocuments.addBeneficiary"),
      remove: t("paymentDocuments.removeBeneficiary")
    },
    [DOCUMENT_GROUP_KEYS.LEGAL_REPRESENTATION]: {
      title: t("paymentDocuments.legalRepresentation"),
      add: t("paymentDocuments.addLegalRepresentative"),
      remove: t("paymentDocuments.removeLegalRepresentative")
    }
  };

  return (
    <ContainerWrapper>
      {/* Capped and centred: left to fill the window, the slot label, the drop
          hint and the actions drift to opposite edges and stop reading as one
          row. */}
      <Box
        sx={{
          maxWidth: "880px",
          mx: "auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 3
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Typography
            sx={{
              ...typography.heading.xl,
              color: semanticColors.text.primary,
              textAlign: "center"
            }}
          >
            {t("paymentDocuments.title")}
          </Typography>
          <HelpButton linkName={PAYMENTS_HELP_CONTENT.CONFIG_ADD_DOCS} />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Link
            href={PAYMENTS_MANUAL_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("paymentDocuments.manual")}
          </Link>
        </Box>

        <ConfigurationSection
          title={t("paymentConfigurationDetail.documentsSection")}
          valid={requiredDocuments.every(documentType =>
            findSingleDocument(configuration.easypayDocuments, documentType)
          )}
        >
          {requiredDocuments.map(documentType => (
            <DocumentUploadField
              key={documentType}
              configId={configId}
              documentType={documentType}
              idGroup={SINGLE_DOCUMENT_GROUP_ID}
              document={findSingleDocument(
                configuration.easypayDocuments,
                documentType
              )}
              onChange={refetch}
              readOnly={readOnly}
            />
          ))}
        </ConfigurationSection>

        {requiredGroups.map(groupKey => (
          <DocumentGroupSection
            key={groupKey}
            configId={configId}
            groupKey={groupKey}
            title={groupLabels[groupKey].title}
            addLabel={groupLabels[groupKey].add}
            removeLabel={groupLabels[groupKey].remove}
            entryLabel={groupLabels[groupKey].title}
            entries={entriesFor(groupKey)}
            onAddEntry={() => addEntry(groupKey)}
            onRemoveEntry={entry => removeEntry(groupKey, entry)}
            onChange={refetch}
            readOnly={readOnly}
          />
        ))}

        {error && <Alert severity="error">{error}</Alert>}
        {readOnly && (
          <Alert severity="info">
            {t(
              configuration.easypayState === "DONE"
                ? "paymentDocuments.closedApproved"
                : "paymentDocuments.closedReviewing"
            )}
          </Alert>
        )}
        {!readOnly && !allDocumentsUploaded && (
          <Typography
            sx={{
              ...typography.body.m,
              color: semanticColors.text.secondary,
              textAlign: "center"
            }}
          >
            {t("paymentDocuments.missingDocuments")}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <ActionButton
            label={t("paymentConfigurations.back")}
            onClick={() => history.push(configurationPath(configId))}
            variant="secondary"
          />
          {!readOnly && (
            <ActionButton
              label={t("paymentDocuments.confirm")}
              onClick={onConfirm}
              loading={confirming}
              disabled={!allDocumentsUploaded || confirming}
            />
          )}
        </Box>
      </Box>
    </ContainerWrapper>
  );
}
